import {computed, ref, watch} from "@vue/composition-api";
import store from "@/store";
import {useToast} from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default function useClientsList() {
    const toast = useToast();
    const selectedStatus = ref(null);
    const selectedType = ref(null);
    const clientStats = ref(null);
    const refClientsListTable = ref(null);

    const tableColumns = [
        {key: 'name', label: 'Nom du Client', sortable: true},
        {key: 'bids_count', label: 'Nbr. Offres', sortable: true},
        {key: 'projects_count', label: 'Nbr. Projets', sortable: true},
        {key: 'status', label: 'Statut'},
        {
            key: 'details',
            label: 'Adresse',
            formatter: (details) => (details && details.address) ? details.address : '----'
        },
        {key: 'actions', label: 'Actions'},
    ];
    const statusVariant = {
        active: 'success',
        inactive: 'secondary',
    };
    const showDeleteModal = ref(false);
    const clientToDelete = {
        id: null,
        name: '',
    };

    const searchQuery = ref('');
    const perPage = ref(10);
    const perPageOptions = [10, 25, 50, 100];
    const currentPage = ref(1);
    const sortBy = ref("created_at");
    const isSortDirDesc = ref(true);

    const statusOptions = [
        {value: null, label: 'Tous les statuts'},
        {value: 'active', label: 'Actif'},
        {value: 'inactive', label: 'Inactif'},
    ];

    const typeOptions = [
        {value: null, label: 'Tous les types'},
        {value: 'individual', label: 'Particulier'},
        {value: 'company', label: 'Entreprise'},
    ];
    const clients = computed(() => store.getters['client/all']);
    const loading = computed(() => store.getters['client/loading']);

    const dataMeta = computed(() => store.getters['client/meta'])

    const paginationInfo = computed(() => {
        const {from, to, total} = dataMeta.value;
        return `${from} - ${to} sur ${total}`;
    })

    const refreshClientsList = () => {
        refClientsListTable.value.refresh();
    }

    watch([currentPage, perPage, searchQuery, selectedStatus, selectedType, sortBy, isSortDirDesc], () => {
        refreshClientsList();
    })

    const fetchClients = (ctx, callback) => {
        store
            .dispatch('client/fetchClientsList', {
                q: searchQuery.value,
                perPage: perPage.value,
                page: currentPage.value,
                sortBy: sortBy.value,
                sortDesc: isSortDirDesc.value,
                status: selectedStatus.value,
                type: selectedType.value,
            }).then(() => {
                callback(clients.value);
        }).catch((error) => {
            toast({
                component: ToastificationContent,
                props: {
                    title: "Erreur lors de la récupération des clients.",
                    icon: "AlertTriangleIcon",
                    variant: "danger"
                }
            })
        })
    }

    const handleDeleteClient = async (id) => {
        try {
            await store.dispatch('client/deleteClient', id)
        } catch (error) {
            toast({
                component: ToastificationContent,
                props: {
                    title: "Erreur lors de la suppression du client.",
                    icon: "AlertTriangleIcon",
                    variant: "danger"
                }
            })
        }
    }

    const toggleClientStatus = (checked, id) => {
        store
            .dispatch("client/toggleClientStatus", {id: id, checked: checked})
            .then((response) => {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: "Le statut du client a été mise à jour avec succès",
                        icon: "AlertTriangleIcon",
                        variant: "success",
                    },
                });
                refreshClientsList();
            })
            .catch(() => {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: "Erreur lors de la mise à jour du statut du rôle",
                        icon: "AlertTriangleIcon",
                        variant: "danger",
                    },
                });
            });
    };

    const getDetailsData = (details, key) => {
        if(!details) return '----';
        return details.hasOwnProperty(key) ? details[key] : '----';
    };

    return {
        searchQuery,
        perPage,
        perPageOptions,
        currentPage,
        sortBy,
        isSortDirDesc,
        paginationInfo,
        statusOptions,
        typeOptions,
        dataMeta,
        selectedStatus,
        selectedType,
        refClientsListTable,
        clientStats,
        tableColumns,
        clients,
        loading,
        fetchClients,
        toggleClientStatus,
        showDeleteModal,
        handleDeleteClient,
        getDetailsData,
    }
}